import React, { useRef, useState, useEffect } from "react";


export type InputMode = "numeric" | "alpha" | "alphanumeric";

interface SegmentedInputProps {
  id?: string | string[];
  className?: string | string[];
  length: number;
  mode?: InputMode;
  onChange?: (value: string, isComplete: boolean) => void;
}

const regexMap: Record<InputMode, RegExp> = {
  numeric: /^[0-9]$/,
  alpha: /^[a-zA-Z]$/,
  alphanumeric: /^[a-zA-Z0-9]$/,
};

export const SegmentedForm = (props: SegmentedInputProps) => {
  const {
    id,
    className,
    length,
    mode = "numeric",
    onChange,
  } = props;
  const [values, setValues] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const regex = regexMap[mode];

  useEffect(() => {
    const value = values.join("");
    const isComplete = values.every((v) => v.length === 1);
    onChange?.(value, isComplete);
  }, [values, onChange]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      inputsRef.current[index]?.focus();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
  const val = e.target.value;

  if (!val) {
    updateValue(index, "");
    return;
  }

  const char = val.slice(-1);
    if (!regex.test(char)) return;
    updateValue(index, char);
    if (index < length - 1) {
    focusInput(index + 1);
    }
  };

  const updateValue = (index: number, char: string) => {
    setValues((prev) => {
      const newValues = [...prev];
      newValues[index] = char;
      return newValues;
    });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !values[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;
    const filtered = pasted
      .split("")
      .filter((c) => regex.test(c));
    if (filtered.length === 0) return;

    setValues((prev) => {
      const newValues = [...prev];
      let pasteIndex = index;

      filtered.forEach((char) => {
        if (pasteIndex < length) {
          newValues[pasteIndex] = char;
          pasteIndex++;
        }
      });

      setTimeout(() => {
        const firstEmpty = newValues.findIndex((v) => v === "");
        if (firstEmpty !== -1) {
          focusInput(firstEmpty);
        } else {
          focusInput(length - 1);
        }
      }, 0);

      return newValues;
    });
  };

  return (
    <>
      {Array.from({ length }).map((_, index) => (
        <input
          id={Array.isArray(id) ? id[index] : `${id}${index}`}
          className={Array.isArray(className) ? className[index] : `${className}${index}`}
          key={index}
          ref={(el) => (inputsRef.current[index] = el)}
          type="text"
          inputMode={mode === "numeric" ? "numeric" : "text"}
          maxLength={1}
          value={values[index]}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          autoComplete="off"
        />
      ))}
    </>
  );
};